import { useCallback } from 'react';
import { useToast } from './useToast';
import { useI18n } from '../i18n';
import { formatWarnings, type ApiWarning } from './useApiCall';
import { logBackgroundError } from '../observability';
import { isSignOutRejection } from '../lib/signOuts';

/** What the user must read after a write that committed. */
export interface AfterWriteResult {
  /** The place of the call, for the telemetry of a failed refresh: `<source>:refresh`. */
  source: string;
  /** The text of the success. It shows after the refresh worked. */
  success?: string;
  /** `warnings[]` of the answer. A caller that lets the warnings replace the success omits `success`. */
  warnings?: ApiWarning[] | null;
  /** The read that shows the new state. It must reject when the read fails. */
  refresh?: () => Promise<unknown>;
}

/**
 * The result of a write that committed (#419, #605, #664). Call it after the
 * write, inside the `run()` of the lock. It returns `true` when the refresh
 * worked (also when there is none) and `false` when the refresh failed.
 *
 *   1. The warnings of the answer show at once, as ONE warning toast, each
 *      warning through `formatWarning()`. With a `success`, the toast reads
 *      "success — warnings".
 *   2. `refresh()` runs. When it fails, the failure goes to the telemetry and
 *      ONE warning replaces the toast: the warnings, then
 *      `t.common.listNotRefreshed` ("Saved. The list did not refresh"). A
 *      second warning toast replaces the first, so the warnings must be in the
 *      text of the second. The write committed, so this is never an error toast.
 *   3. The success shows only when the refresh worked, and only when there are
 *      no warnings.
 *   4. #666 — When a sign-out ended the refresh, no toast shows. The user
 *      signs out, and the result is `false`.
 *
 * The write itself stays in a `try` with `showError(err)`: a refusal of the
 * write is an error. Call it inside <ToastProvider> (useToast() throws outside it).
 */
export function useAfterWrite(): (result: AfterWriteResult) => Promise<boolean> {
  const { showSuccess, showWarning } = useToast();
  const { t } = useI18n();
  return useCallback(async ({ source, success, warnings, refresh }: AfterWriteResult) => {
    const advice = formatWarnings(warnings);
    if (advice) showWarning(success ? `${success} — ${advice}` : advice);
    try {
      await refresh?.();
    } catch (err) {
      if (isSignOutRejection(err)) return false;
      logBackgroundError(`${source}:refresh`)(err);
      showWarning(advice ? `${advice} · ${t.common.listNotRefreshed}` : t.common.listNotRefreshed);
      return false;
    }
    if (success && !advice) showSuccess(success);
    return true;
  }, [showSuccess, showWarning, t]);
}
